import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type UiV2State } from '../lib/api';
import { useApp } from '../store';
import { NEWS_VERSION } from '../lib/news';
import { useGameKeyboardShortcuts } from '../hooks/useGameKeyboardShortcuts';
import { useClientUpdateCheck } from '../hooks/useClientUpdateCheck';
import { ClientUpdateBanner } from '../components/ClientUpdateBanner';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { GameShellV1 } from '../ui/v1/GameShellV1';
import { UiVersionProvider } from '../ui/v2/UiVersionContext';
import { useViewportMode } from '../ui/v2/useViewportMode';

const MapView = lazy(() =>
  import('../components/MapView').then((m) => ({ default: m.MapView })),
);

const GameShellV2 = lazy(async () => {
  await import('../styles/ui-v2.css');
  const mod = await import('../ui/v2/GameShellV2');
  return { default: mod.GameShellV2 };
});

function readForceV1Query(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('ui') === 'v1';
  } catch {
    return false;
  }
}

export function App() {
  const { t } = useTranslation('panels');
  const {
    user,
    panel,
    tutorialOpen,
    setUser,
    setWallet,
    setZones,
    setPanel,
    setTutorialOpen,
    setNewsOpen,
  } = useApp();
  const [booting, setBooting] = useState(true);
  const [forceV1] = useState(readForceV1Query);
  const [guestUi, setGuestUi] = useState<UiV2State | null>(null);
  const viewport = useViewportMode();
  useGameKeyboardShortcuts();
  const { updateAvailable, reload } = useClientUpdateCheck();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.me();
        if (cancelled) return;
        if (me.user) {
          setUser(me.user);
          setWallet(me.wallet);
          setZones(me.zones ?? null);
          setGuestUi(null);
          if (me.user.needsEmpireName) setPanel('profile');
          else if (!me.user.tutorialSeen) setTutorialOpen(true);
        } else {
          if (me.ui) setGuestUi(me.ui);
          const result = await api.trySilentSso();
          if (result === 'redirect') return;
        }
      } catch {
        /* ignore boot errors */
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setPanel, setUser, setWallet, setZones, setTutorialOpen]);

  useEffect(() => {
    if (user && !user.needsEmpireName && !user.tutorialSeen && panel === 'none') {
      setTutorialOpen(true);
    }
  }, [user, panel, setTutorialOpen]);

  useEffect(() => {
    if (
      user
      && !user.needsEmpireName
      && user.tutorialSeen
      && !tutorialOpen
      && panel === 'none'
      && (user.newsSeenVersion ?? 0) < NEWS_VERSION
    ) {
      setNewsOpen(true);
    }
  }, [user, panel, tutorialOpen, setNewsOpen]);

  const ui: UiV2State | null = user?.ui ?? guestUi;
  const canUseV2 = ui?.canUseV2 === true;
  const serverVersion = ui?.effectiveVersion === 'v2' ? 'v2' : 'v1';
  // Kill-switch ?ui=v1 : honoré uniquement si canUseV2 (admin / flag).
  const effectiveVersion: 'v1' | 'v2' =
    canUseV2 && forceV1 ? 'v1' : serverVersion;

  const providerProps = useMemo(
    () => ({
      version: effectiveVersion,
      ui,
      forceV1: canUseV2 && forceV1,
      isMobile: viewport.isMobile,
      isNarrowTablet: viewport.isNarrowTablet,
    }),
    [effectiveVersion, ui, canUseV2, forceV1, viewport.isMobile, viewport.isNarrowTablet],
  );

  if (booting) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="font-[family-name:var(--font-display)] text-3xl text-[var(--accent)]">GeoLadder</div>
          <p className="mt-2 text-sm text-[var(--muted)]">{t('app.loadingMap')}</p>
        </div>
      </div>
    );
  }

  return (
    <UiVersionProvider {...providerProps}>
      <div className="relative h-full w-full overflow-hidden">
        <ErrorBoundary label="map">
          <Suspense fallback={null}>
            <MapView />
          </Suspense>
        </ErrorBoundary>
        {updateAvailable && <ClientUpdateBanner onReload={reload} />}
        <ErrorBoundary label="shell">
          {effectiveVersion === 'v2' ? (
            <Suspense fallback={null}>
              <GameShellV2 />
            </Suspense>
          ) : (
            <GameShellV1 />
          )}
        </ErrorBoundary>
      </div>
    </UiVersionProvider>
  );
}
